import { generateGuid } from "@1js/guid";
import type { ProductType } from "@1js/pl-card-components";
import { authAwareFetch } from "./fetch";

/**
 * Sample Response,
 * type documented on https://eng.ms/docs/experiences-devices/opg/office-ai/app-copilot/onboarding/handoff
 * HTTP/1.1 200 OK
 * Content-type: application/json
 * {
 *   "responses": [{
 *     "destinationApp": "word",
 *     "language": "en-us",
 *     "generatedLinks": [{
 *       "appPlatform": "Web",
 *       "platformHandoffUrl": "<web url>"
 *     }],
 *     "message": "<user display message>",
 *     "H_": {
 *       "T_": "AugLoop_HandoffSkill_HandoffOutboundMessage",
 *       "B_": ["AugLoop_Core_Annotation"]
 *     },
 *     "M_": { "seq": 2 }
 *   }]
 * }
 */
export interface IGetAnnotationsResponseMessage {
  destinationApp: string;
  language: string;
  generatedLinks: {
    appPlatform: string;
    platformHandoffUrl: string;
  }[];
  message: string;
}

export interface IHandoffErrorResponseMessage {
  error: string;
}

export type HandoffResponse =
  | IGetAnnotationsResponseMessage
  | IHandoffErrorResponseMessage;

export const handoffAsync = async (
  host: string,
  locale: string,
  product: ProductType | string,
  query: string,
  scenarioName?: string,
  documentUrl?: string,
  context?: string
): Promise<HandoffResponse> => {
  if (["Word", "Excel", "PowerPoint"].indexOf(product) === -1) {
    throw new Error("UnknownDestination");
  }

  const queryId = generateGuid();
  let body: object = {
    queryId,
    destinationApp: product,
    destinationDocUrl: documentUrl,
    language: locale,
    manualSendPrompt: true,
    scenarioName: scenarioName || "new",
    query,
  };

  if (context) {
    body = {
      ...body,
      context,
    };
  }

  const serviceUrl = `https://${host}/${locale}/api/promptsclient/handoff/augloop`;
  const response = await authAwareFetch(serviceUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      RequestVerificationToken:
        document
          ?.querySelector('input[name="__RequestVerificationToken"]')
          ?.getAttribute("value") ?? "",
    },
    body: JSON.stringify(body),
  });

  return (await response.json()) as IGetAnnotationsResponseMessage;
};
